// Sibling presentational pieces for CreateVariantForm — pulled out
// of the main file so it stays under the 500-line cap. These have
// no internal state of their own; the parent form owns the
// selected value + dispatch flow and feeds the values down.

import type { ReactElement } from 'react'

import {
  GROUPING_CONVENTION,
  GROUPING_CONVENTION_VALUES,
} from '@citycare/shared'
import type { GroupingConvention } from '@citycare/shared'

const GROUPING_LABEL: Record<GroupingConvention, string> = {
  [GROUPING_CONVENTION.PER_SPACE]: '1 asset / space',
  [GROUPING_CONVENTION.PER_BUILDING]: '1 asset / building',
  [GROUPING_CONVENTION.PER_SITE]: '1 asset / site',
}

// Sub-line copy for each grouping tile so the user understands the
// data-model implication of the choice without leaving the form.
const GROUPING_HINT: Record<GroupingConvention, string> = {
  [GROUPING_CONVENTION.PER_SPACE]: 'One row per room',
  [GROUPING_CONVENTION.PER_BUILDING]: 'Shared across rooms',
  [GROUPING_CONVENTION.PER_SITE]: 'Shared site-wide',
}

export interface GroupingRadioTilesProps {
  legendId: string
  value: GroupingConvention
  onChange: (next: GroupingConvention) => void
  disabled: boolean
}

// Stack of radio tiles for the "Grouping convention" field. Each
// tile carries a primary label and a one-line hint so the user
// can pick correctly without having to consult docs.
export function GroupingRadioTiles({
  legendId,
  value,
  onChange,
  disabled,
}: GroupingRadioTilesProps): ReactElement {
  return (
    <fieldset disabled={disabled}>
      <legend
        id={legendId}
        className="mb-2 flex items-baseline gap-1 text-[12px] font-medium uppercase tracking-[0.14em] text-cc-forest/70"
      >
        Grouping Convention
        <span aria-hidden="true" className="text-[12px] text-cc-orange">
          *
        </span>
      </legend>
      <div
        role="radiogroup"
        aria-labelledby={legendId}
        className="space-y-2"
      >
        {GROUPING_CONVENTION_VALUES.map((option) => {
          const active = value === option
          return (
            <label
              key={option}
              className={`flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                active
                  ? 'border-cc-forest bg-cc-cream/60'
                  : 'border-cc-forest/15 bg-white hover:border-cc-forest/40'
              } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              <input
                type="radio"
                name="grouping-convention"
                value={option}
                checked={active}
                onChange={() => onChange(option)}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                  active ? 'border-cc-forest' : 'border-cc-forest/25'
                }`}
              >
                {active ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-cc-forest" />
                ) : null}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-[13px] font-medium text-cc-forest">
                  {GROUPING_LABEL[option]}
                </span>
                <span className="mt-0.5 text-[11px] text-cc-forest/55">
                  {GROUPING_HINT[option]}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

export interface CollisionPromptProps {
  headingId: string
  collidedName: string
  subSystemName: string
  onAdopt: () => void
  onDismiss: () => void
}

// Shown when the server returns 409 already_exists — the field-
// staff user is offered the chance to adopt the existing
// permanent asset-type id rather than create a duplicate
// field-added row.
export function CollisionPrompt({
  headingId,
  collidedName,
  subSystemName,
  onAdopt,
  onDismiss,
}: CollisionPromptProps): ReactElement {
  return (
    <section
      aria-labelledby={headingId}
      className="space-y-4 px-6 py-6"
    >
      <h3
        id={headingId}
        className="font-display text-[22px] leading-tight text-cc-forest"
      >
        This asset type already exists
      </h3>
      <p className="text-sm text-cc-forest/70">
        <span className="font-medium text-cc-forest">{collidedName}</span> is
        already registered under{' '}
        <span className="font-medium text-cc-forest">{subSystemName}</span>.
        Add it to this room?
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onAdopt}
          className="inline-flex min-h-11 items-center gap-2 rounded-pill bg-cc-forest px-5 py-2.5 text-[13px] font-medium text-cc-cream hover:bg-cc-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-lime"
        >
          Add to room
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex min-h-11 items-center rounded-pill border border-cc-forest/15 bg-white px-5 py-2.5 text-[13px] font-medium text-cc-forest hover:bg-cc-forest/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cc-forest"
        >
          Back
        </button>
      </div>
    </section>
  )
}
